// Narration tab of the F-F media panel. One row per narration language
// (TC=Cantonese, SC=Mandarin, EN; per ADR-0024 D4). Each row shows the
// current source, an audio preview when stored, and the actions:
// Generate/Regenerate (MiniMax, synchronous in v1), Download (fetch the
// stored audio to edit externally), Upload custom (replaces), and Remove
// (clears the ref → falls back to voice_url / browser TTS).
//
// Presentational + controlled: the container owns the mutations. Source is
// derived from the entity response (narration_asset_id_* / voice_url_* /
// narration_source_*): stored audio badges as "AI-generated" or "Custom
// audio" via the slot-level provenance flag, falling back to the generic
// "Stored audio" for legacy rows that predate the flag.
//
// #2: when the description for a language is edited while narration audio is
// already in place, the row shows a live, dismissible nudge (client-side only —
// `descriptionChanged` is computed by the form from current vs saved text).

import { useState } from "react";
import { Download, Loader2, Sparkles, Trash2, Upload } from "lucide-react";

import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import type { NarrationSource } from "@shared/api/exhibits";
import type { AudioLanguage } from "@shared/hooks/useTextToSpeech";

import FileButton from "./FileButton";

export interface NarrationRow {
  assetId: string | null;
  voiceUrl: string;
  // Provenance of the stored asset (AI-generated vs custom upload); null /
  // absent on rows that predate the flag → generic "Stored audio" badge.
  source?: NarrationSource | null;
  // #2: the description for this language was edited after narration audio was
  // put in place — a live nudge to re-check the audio. Dismissible per language.
  descriptionChanged?: boolean;
}

export interface NarrationTabProps {
  ownerKey: string;
  assetUrl: (assetId: string) => string;
  narration: Record<AudioLanguage, NarrationRow>;
  generatingLang: AudioLanguage | null;
  disabled?: boolean;
  onGenerate: (lang: AudioLanguage) => void;
  onUploadCustom: (lang: AudioLanguage, file: File) => void;
  // Download the stored narration audio (to edit externally, then re-upload).
  onDownload: (lang: AudioLanguage) => void;
  onRemove: (lang: AudioLanguage) => void;
  // Edit/save a legacy voice URL for a language (clears it when saved empty).
  onSaveVoiceUrl: (lang: AudioLanguage, url: string) => void;
}

const LANGS: { lang: AudioLanguage; heading: string }[] = [
  { lang: "tc", heading: "繁體中文 (Cantonese)" },
  { lang: "en", heading: "English" },
  { lang: "sc", heading: "简体中文 (Mandarin)" },
];

function sourceBadge(row: NarrationRow): { label: string; className: string } {
  if (row.assetId) {
    if (row.source === "ai") {
      return {
        label: "AI-generated",
        className: "bg-violet-100 text-violet-800",
      };
    }
    if (row.source === "custom") {
      return { label: "Custom audio", className: "bg-blue-100 text-blue-800" };
    }
    // Legacy rows stored before the provenance flag existed.
    return {
      label: "Stored audio",
      className: "bg-green-100 text-green-800",
    };
  }
  if (row.voiceUrl) {
    return { label: "Legacy URL", className: "bg-amber-100 text-amber-800" };
  }
  return { label: "Browser TTS", className: "bg-gray-100 text-gray-700" };
}

const NarrationTab = ({
  ownerKey,
  assetUrl,
  narration,
  generatingLang,
  disabled,
  onGenerate,
  onUploadCustom,
  onDownload,
  onRemove,
  onSaveVoiceUrl,
}: NarrationTabProps) => {
  // Per-language local dismissal of the "description changed" nudge (#2). Live
  // and client-side only — resets when the form is reopened.
  const [dismissed, setDismissed] = useState<
    Partial<Record<AudioLanguage, boolean>>
  >({});
  // Per-language draft of the legacy voice URL being edited (committed on Save).
  const [urlDrafts, setUrlDrafts] = useState<
    Partial<Record<AudioLanguage, string>>
  >({});

  return (
    <div className="space-y-4" data-testid="narration-tab">
      {LANGS.map(({ lang, heading }) => {
        const row = narration[lang];
        const badge = sourceBadge(row);
        const generating = generatingLang === lang;
        const busy = disabled || generating;
        // Narration is "in place" via a stored asset OR a legacy voice URL —
        // both were made for the old text and can go stale. Browser-TTS (neither)
        // reads the live description, so it's never stale.
        const hasNarration = !!row.assetId || !!row.voiceUrl;
        const showChangedNudge =
          hasNarration && !!row.descriptionChanged && !dismissed[lang];
        return (
          <div
            key={lang}
            className="space-y-3 p-4 bg-white border border-border rounded-lg"
            data-testid={`narration-row-${lang}`}
          >
            <div className="flex items-center justify-between">
              <h4 className="font-heading font-semibold">{heading}</h4>
              <span
                className={`text-xs px-2 py-1 rounded ${badge.className}`}
                data-testid={`narration-source-${lang}`}
              >
                {badge.label}
              </span>
            </div>

            {!row.assetId && (
              <div className="flex items-center gap-2">
                <Input
                  value={urlDrafts[lang] ?? row.voiceUrl}
                  onChange={(e) =>
                    setUrlDrafts((d) => ({ ...d, [lang]: e.target.value }))
                  }
                  disabled={busy}
                  placeholder="Audio URL (optional)"
                  className="font-mono text-xs"
                  data-testid={`narration-url-${lang}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={
                    busy || (urlDrafts[lang] ?? row.voiceUrl) === row.voiceUrl
                  }
                  onClick={() =>
                    onSaveVoiceUrl(lang, urlDrafts[lang] ?? row.voiceUrl)
                  }
                  data-testid={`narration-url-save-${lang}`}
                >
                  Save URL
                </Button>
              </div>
            )}

            {showChangedNudge && (
              <div
                className="flex items-start justify-between gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
                data-testid={`narration-stale-${lang}`}
              >
                <span>
                  ⚠ The description changed after this narration audio was put
                  in place — you may want to check it&apos;s up to date.
                </span>
                <button
                  type="button"
                  onClick={() => setDismissed((d) => ({ ...d, [lang]: true }))}
                  className="shrink-0 underline text-amber-700"
                  data-testid={`narration-stale-dismiss-${lang}`}
                >
                  Dismiss
                </button>
              </div>
            )}

            {(() => {
              // Player for whatever is currently playable: the stored narration
              // asset, else the legacy voice_url. None → browser TTS (no file to
              // preview here).
              const previewSrc = row.assetId
                ? assetUrl(row.assetId)
                : row.voiceUrl || null;
              return previewSrc ? (
                <audio
                  controls
                  preload="none"
                  src={previewSrc}
                  className="w-full"
                  data-testid={`narration-preview-${lang}`}
                />
              ) : null;
            })()}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => onGenerate(lang)}
                data-testid={`narration-generate-${lang}`}
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-1" />
                )}
                {generating
                  ? "Generating…"
                  : row.assetId
                    ? "Regenerate"
                    : "Generate"}
              </Button>

              {row.assetId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => onDownload(lang)}
                  data-testid={`narration-download-${lang}`}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
              )}

              <FileButton
                accept="audio/mpeg,audio/mp4,audio/x-wav,audio/wav"
                onFile={(file) => onUploadCustom(lang, file)}
                disabled={busy}
                testId={`narration-upload-${lang}`}
              >
                <Upload className="w-4 h-4 mr-1" />
                Upload custom
              </FileButton>

              {hasNarration && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => onRemove(lang)}
                  data-testid={`narration-remove-${lang}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove
                </Button>
              )}
            </div>
          </div>
        );
      })}
      <p
        className="text-xs text-muted-foreground"
        data-testid={`narration-help-${ownerKey}`}
      >
        Generate uses the configured voice for each language. To edit a
        narration, Download it, edit the file externally, then Upload custom to
        replace it. Removing stored audio falls back to the legacy voice URL,
        then the browser&apos;s built-in text-to-speech.
      </p>
    </div>
  );
};

export default NarrationTab;
